import { Component, OnInit, OnDestroy, ViewEncapsulation, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MessageModel } from "../../../../../models/message.model";
import { AuthService } from "../../../../../services/auth.service";
import { MessageService } from "../../../../../services/message.service";
import { UtilityService } from "../../../../../services/utility.service";
import { MessageFormComponent } from "../message-form.component";
import { ActivatedRoute } from "@angular/router";
import { MessageFormProperty } from "../../../../../types/message-types";
import { Subject, takeUntil, filter } from "rxjs";

@Component({
  selector: "app-web-message-form",
  standalone: false,
  templateUrl: "./web-message-form.component.html",
  styleUrls: ["./../message-form.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None
})
export class WebMessageFormComponent extends MessageFormComponent implements OnInit, OnDestroy {
  readonly maxLength = {
    body: 200,
    heading: 100,
  };
  
  private destroy$ = new Subject<void>();

  constructor(
    protected override authService: AuthService,
    protected override messageService: MessageService,
    protected override activatedRoute: ActivatedRoute
  ) {
    super(authService, messageService, activatedRoute);
  }

  override ngOnInit() {
    this.messageService.changeMessageType('TOT');
    
    // Subscribe to auth service ready state
    this.authService.userGroupsReady$
      .pipe(
        filter(ready => ready === true),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.applyReadOnlyState();
      });

    this.messageService.applyKlart
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyKlart());
  }
  
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  override setUpExistingMessage(val: MessageModel) {
    val.validFrom = new Date(val.validFrom!);
    val.validFrom.setSeconds(0);

    if (UtilityService.validDate(val.validTo!)) {
      val.validTo = new Date(val.validTo!);
      val.validTo.setSeconds(0);
    } else {
      val.validTo = null;
    }

    this._message = val;
    this.setUpFormControls();
  }

  override setUpDefaultValues(val: MessageModel) {
    val.validFrom = new Date();
    val.type = "TOT";

    this._message = val;
    this.setUpFormControls();
  }

  setUpFormControls() {
    this.messageForm = new FormGroup({
      body: new FormControl({
        value: this.message.body,
        disabled: false
      }),
      heading: new FormControl({
        value: this.message.heading,
        disabled: false
      }),
      validFrom: new FormControl({
        value: this.message.validFrom,
        disabled: false
      }),
      validTo: new FormControl({
        value: this.message.validTo,
        disabled: this.isReadOnly()
      }),
      timeIntervalStart: new FormControl({
        value: this.getTimeAsDate(this.message.timeIntervalStart),
        disabled: this.isReadOnly() || !this.message.timeIntervalEnabled
      }),
      timeIntervalEnd: new FormControl({
        value: this.getTimeAsDate(this.message.timeIntervalEnd),
        disabled: this.isReadOnly() || !this.message.timeIntervalEnabled
      })
    });

    this.setInitialErrors("heading");
    this.setInitialErrors("body");

    if (this.authService.graphUserGroups) {
      this.applyReadOnlyState();
    }
  }

  onTimeIntervalToggle() {
    this.message.timeIntervalEnabled = !this.message.timeIntervalEnabled;
    this.syncTimeIntervalState();
  }

  syncTimeIntervalState() {
    const enabled = this.message.timeIntervalEnabled;

    if (this.messageService.currentMessage) {
      this.messageService.currentMessage.timeIntervalEnabled = enabled;
    }

    const startCtrl = this.messageForm?.get('timeIntervalStart');
    const endCtrl = this.messageForm?.get('timeIntervalEnd');

    if (!enabled) {
      this.message.timeIntervalStart = "";
      this.message.timeIntervalEnd = "";
      startCtrl?.patchValue(null, { emitEvent: false });
      endCtrl?.patchValue(null, { emitEvent: false });

      if (this.messageService.currentMessage) {
        this.messageService.currentMessage.timeIntervalStart = "";
        this.messageService.currentMessage.timeIntervalEnd = "";
      }

      if (!this.isReadOnly()) {
        startCtrl?.disable({ emitEvent: false });
        endCtrl?.disable({ emitEvent: false });
      }
    } else if (!this.isReadOnly()) {
      startCtrl?.enable({ emitEvent: false });
      endCtrl?.enable({ emitEvent: false });
    }
  }

  onTimeIntervalClear(type: 'start' | 'end') {
    if (type === 'start') {
      this.message.timeIntervalStart = "";
      this.messageForm.get('timeIntervalStart')?.patchValue(null, { emitEvent: false });
      if (this.messageService.currentMessage) {
        this.messageService.currentMessage.timeIntervalStart = "";
      }
    } else {
      this.message.timeIntervalEnd = "";
      this.messageForm.get('timeIntervalEnd')?.patchValue(null, { emitEvent: false });
      if (this.messageService.currentMessage) {
        this.messageService.currentMessage.timeIntervalEnd = "";
      }
    }
  }

  onTimeIntervalChange(date: Date | null, type: 'start' | 'end') {
    if (date && date instanceof Date) {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const timeString = `${hours}:${minutes}:00`;
      
      if (type === 'start') {
        this.message.timeIntervalStart = timeString;
        this.messageForm.get('timeIntervalStart')?.patchValue(date, { emitEvent: false });
        if (this.messageService.currentMessage) {
          this.messageService.currentMessage.timeIntervalStart = timeString;
        }
      } else {
        this.message.timeIntervalEnd = timeString;
        this.messageForm.get('timeIntervalEnd')?.patchValue(date, { emitEvent: false });
        if (this.messageService.currentMessage) {
          this.messageService.currentMessage.timeIntervalEnd = timeString;
        }
      }
    } else if (date === null) {
      this.onTimeIntervalClear(type);
    }
  }

  getTimeAsDate(timeString: string): Date | null {
    if (!timeString) return null;
    
    // Parse "HH:MM:SS" or "HH:MM"
    const parts = timeString.split(':');
    if (parts.length < 2) return null;
    
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    
    // Create a Date object with today's date but specified time
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    
    return date;
  }

  setToday(property: MessageFormProperty) {
    const now = new Date();
    now.setSeconds(0);
    this.message[property] = now;
    this.messageForm.get(property)?.patchValue(now);
    this.onDateChange(now, property);
  }

  clearDate(property: MessageFormProperty) {
    this.message[property] = null;
    this.messageForm.get(property)?.patchValue(null);
    this.onDateChange(null, property);
  }

  private applyKlart() {
    if (this.isReadOnly() || !this.message?.id) {
      return;
    }

    this.setTextField(
      'heading',
      this.prependKlart(this.message.heading),
      { required: true, maxLimit: this.maxLength.heading }
    );
    this.setTextField(
      'body',
      this.prependKlart(this.message.body),
      { required: true, maxLimit: this.maxLength.body, domId: 'bodyDiv' }
    );
  }
}
